// src/components/order/OrderCard.tsx
import React, { useState } from "react";
import { Order } from "../../../../__generated__/graphql";
import { FaCopy, FaCheck } from "react-icons/fa";
import { notify } from "../../../../utils/toast";
import { useQuery } from "@apollo/client";
import { gql } from "../../../../__generated__";

interface OrderCardProps {
  order: Order;
  countdown: string | null;
  handleConfirmOrder: (orderId: number) => void;
  handleCancelOrder: (orderId: number) => void;
  handleCompletePickup: (orderId: number) => void;
  isPickupCompleted: boolean | null;
  isTomorrowOrder: (order: Order) => boolean;
  isCancelSelectionModeStarted?: boolean; // 선택하여 취소하기 모드 시작 여부
  isCancelSelected?: boolean; // 선택하여 취소하기 모드에서 선택된 주문 여부
  handleToggleCancelSelection?: () => void; // 주문 취소를 위한 선택 상태를 토글하는 핸들러
  onBlockUser?: (userId: number, userName?: string) => void; // 유저 차단 핸들러
}

export const GET_USER_STORE_SUMMARY = gql(`
  query GetUserStoreSummary($userId: Int!) {
    getUserStoreSummary(userId: $userId) {
      totalOrderCount
      lastPickupDate
      lastReview {
        id
        content
        reviewImages {
          img
        }
      }
    }
  }
`);

const OrderCard: React.FC<OrderCardProps> = ({
  order,
  countdown,
  handleConfirmOrder,
  handleCancelOrder,
  handleCompletePickup,
  isPickupCompleted,
  isTomorrowOrder,
  isCancelSelectionModeStarted,
  isCancelSelected = false,
  handleToggleCancelSelection,
  onBlockUser,
}) => {
  const { data } = useQuery(GET_USER_STORE_SUMMARY, {
    variables: { userId: order.user?.id || 0 },
  });
  const [isOpen, setIsOpen] = useState(false);

  const toggleDetails = () => {
    setIsOpen(!isOpen);
  };

  const isYesterday = (dateString: string) => {
    const date = new Date(dateString);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  };
  const getPickUpTime = (order: Order) => {
    if (!order.packPickupStartTime || !order.packPickupEndTime) return;

    const today = new Date();
    const pickUpDate = new Date(order.packPickupStartTime);
    const pickUpEndDate = new Date(order.packPickupEndTime);

    // 날짜 조정
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const startHour = pickUpDate.getHours().toString().padStart(2, "0");
    const startMinute = pickUpDate.getMinutes().toString().padStart(2, "0");
    const endHour = pickUpEndDate.getHours().toString().padStart(2, "0");
    const endMinute = pickUpEndDate.getMinutes().toString().padStart(2, "0");

    if (today.toDateString() === pickUpDate.toDateString()) {
      return `오늘 ${startHour}:${startMinute} ~ ${endHour}:${endMinute}`;
    } else if (tomorrow.toDateString() === pickUpDate.toDateString()) {
      return `내일 ${startHour}:${startMinute} ~ ${endHour}:${endMinute}`;
    } else if (yesterday.toDateString() === pickUpDate.toDateString()) {
      return `어제 ${startHour}:${startMinute} ~ ${endHour}:${endMinute}`;
    }
  };

  if (isCancelSelectionModeStarted) {
    return (
      <li
        key={order.id}
        className={`shadow-md rounded-lg relative space-y-2 p-4 pb-10 list-none ${
          order.isPackConfirmed
            ? "border-green-500"
            : order.isCanceled
            ? "border-red-500"
            : ""
        } ${
          order.isPackConfirmed && isPickupCompleted
            ? "bg-green-100"
            : "bg-white"
        } ${
          order.isCanceled || order.isPackConfirmed // 취소되거나 확정되면 클릭 불가능
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
        onClick={(e) => {
          if (!order.isCanceled && !order.isPackConfirmed) {
            // 취소 모드일 때 클릭 이벤트 propagation 방지 및 주문 취소 선택 시 UI 토글
            e.stopPropagation(); // 클릭 이벤트 propagation 방지
            handleToggleCancelSelection?.(); // 주문 취소 선택 시 UI 토글
          }
        }}
      >
        <div>
          {!order.isCanceled && !order.isPackConfirmed && (
            <>
              {isTomorrowOrder(order) ? (
                <div className="mb-4 w-full inline-flex flex-col items-center">
                  <div className="text-xl text-gray-500 p-2 rounded-lg inline-block">
                    <span className="text-purple-500 font-bold">
                      내일 픽업주문
                    </span>
                  </div>
                  <p className="text-gray-500 bg-gray-200 p-2 rounded-lg inline-block text-center">
                    내일 상황을 보시고 <br />
                    언제든 취소하셔도 됩니다
                    <br />
                    픽업시간 전까지 취소 가능합니다
                  </p>
                </div>
              ) : (
                <>
                  {countdown && (
                    <div className="text-lg text-gray-500">
                      <span className="text-red-500">
                        {countdown} 후 자동확정 됩니다.
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* 선택 모드일 때 주문 선택되면 체크 아이콘 표시 */}
          {isCancelSelected && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/50 rounded-lg mt-0">
              <div className="bg-blue-500 rounded-full p-4">
                <FaCheck className="text-white h-8 w-8" />
              </div>
            </div>
          )}
        </div>

        {order?.identificationCode && (
          <div className="text-lg text-gray-600 w-full flex items-center justify-between">
            <span className="font-bold w-1/3">주문코드</span>
            <span className="text-lg text-primary font-black w-2/3">
              {order.identificationCode}
            </span>
          </div>
        )}
        {order?.user?.name && (
          <div className="text-lg text-gray-600 w-full flex items-center justify-between">
            <span className="font-bold w-1/3 tracking-wide">
              이&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;름
            </span>
            <span className="text-xl text-gray-600 w-2/3">
              {order?.user?.name}
            </span>
          </div>
        )}

        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3 tracking-wide">
            수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;량
          </span>
          <div className="w-2/3">{order.totalQuantity}팩</div>
        </div>
        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3">전화번호</span>
          <div className="w-2/3 flex items-center">
            <span className="mr-2 flex items-center">
              <span className="w-full underline">
                {order?.user?.phone
                  ? order?.user?.phone.replace(
                      /(\d{3})(\d{4})(\d{4})/,
                      "$1-$2-$3"
                    )
                  : "없음"}
              </span>
            </span>
          </div>
        </div>

        <div className="pt-4 text-lg text-gray-600 w-full flex items-center justify-between mt-2">
          <span className="font-bold w-1/3">픽업시간</span>
          <div className="w-2/3">{getPickUpTime(order)}</div>
        </div>

        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3">주문일자</span>
          <div className="w-2/3">
            {new Date(order.createdAt).toLocaleDateString()}
            {isYesterday(order.createdAt)
              ? " (어제)" // 어제 들어온 주문인지 보여주기 위함
              : ""}
          </div>
        </div>
        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3">주문시간</span>
          <div className="w-2/3">
            {new Intl.DateTimeFormat("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }).format(new Date(order.createdAt))}
          </div>
        </div>

        <div
          className={`pt-4 text-lg w-full flex items-center justify-between ${
            order.isPackConfirmed
              ? "text-green-500"
              : order.isCanceled
              ? "text-red-500"
              : "text-gray-600"
          }`}
        >
          <span className="font-bold w-1/3 tracking-wide">
            상&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;태
          </span>
          <span className="w-2/3">
            {order.isPackConfirmed ? (
              <span className="text-green-500">확정됨</span>
            ) : order.isCanceled ? (
              <span className="text-red-500">
                {"취소됨"}
                <br />
                {order.cancelReason && (
                  <span className="text-gray-600">
                    {"(취소사유: " + order.cancelReason + ")"}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-gray-600">대기중</span>
            )}
          </span>
        </div>
        {order.isPackConfirmed && (
          <div className="flex pt-4 justify-center">
            <div className="flex items-center border px-2 py-1.5 rounded-lg bg-white gap-2">
              <span className="font-bold">손님이 픽업을 완료했나요?</span>
              <button
                className={`flex items-center justify-center w-12 h-6 rounded-full transition-colors duration-300 ${
                  isPickupCompleted ? "bg-green-500" : "bg-gray-300"
                }`}
                onClick={() => {
                  handleCompletePickup(order.id);
                }}
              >
                <span
                  className={`w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${
                    isPickupCompleted
                      ? "translate-x-[12px]"
                      : "translate-x-[-12px]"
                  }`}
                ></span>
              </button>
            </div>
          </div>
        )}
      </li>
    );
  }

  // 일반 모드일 때 (=선택 모드가 아닐때)
  return (
    <li
      key={order.id}
      className={`shadow-md rounded-lg relative space-y-2 p-4 pb-10 list-none ${
        order.isPackConfirmed
          ? "border-green-500"
          : order.isCanceled
          ? "border-red-500"
          : ""
      } ${
        order.isPackConfirmed && isPickupCompleted ? "bg-green-100" : "bg-white"
      }`}
    >
      <div>
        {!order.isCanceled && !order.isPackConfirmed && (
          <>
            {isTomorrowOrder(order) ? (
              <div className="mb-4 w-full inline-flex flex-col items-center">
                <div className="text-xl text-gray-500 p-2 rounded-lg inline-block">
                  <span className="text-purple-500 font-bold">
                    내일 픽업주문
                  </span>
                </div>
                <p className="text-gray-500 bg-gray-200 p-2 rounded-lg inline-block text-center">
                  내일 상황을 보시고 <br />
                  언제든 취소하셔도 됩니다
                  <br />
                  픽업시간 전까지 취소 가능합니다
                </p>
              </div>
            ) : (
              <>
                {countdown && (
                  <div className="text-lg text-gray-500">
                    <span className="text-red-500">
                      {countdown} 후 자동확정 됩니다.
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {order?.identificationCode && (
        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3">주문코드</span>
          <span className="text-lg text-primary font-black w-2/3">
            {order.identificationCode}
          </span>
        </div>
      )}
      {order?.user?.name && (
        <div className="text-lg text-gray-600 w-full flex items-center justify-between">
          <span className="font-bold w-1/3 tracking-wide">
            이&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;름
          </span>
          <span className="text-xl text-gray-600 w-2/3">
            {order?.user?.name}
          </span>
        </div>
      )}

      <div className="text-lg text-gray-600 w-full flex items-center justify-between">
        <span className="font-bold w-1/3 tracking-wide">
          수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;량
        </span>
        <div className="w-2/3">{order.totalQuantity}팩</div>
      </div>
      <div className="text-lg text-gray-600 w-full flex items-center justify-between">
        <span className="font-bold w-1/3">전화번호</span>
        <div className="w-2/3 flex items-center">
          <span
            className="mr-2 flex items-center"
            onClick={() => {
              navigator.clipboard.writeText(order?.user?.phone || "없음");
              notify("복사되었습니다!");
            }}
          >
            <span className="w-full underline">
              {order?.user?.phone
                ? order?.user?.phone.replace(
                    /(\d{3})(\d{4})(\d{4})/,
                    "$1-$2-$3"
                  )
                : "없음"}
            </span>
            <FaCopy className="underline" />
          </span>
        </div>
      </div>

      <div className="pt-4 text-lg text-gray-600 w-full flex items-center justify-between mt-2">
        <span className="font-bold w-1/3">픽업시간</span>
        <div className="w-2/3">{getPickUpTime(order)}</div>
      </div>

      <div className="text-lg text-gray-600 w-full flex items-center justify-between">
        <span className="font-bold w-1/3">주문일자</span>
        <div className="w-2/3">
          {new Date(order.createdAt).toLocaleDateString()}
          {isYesterday(order.createdAt)
            ? " (어제)" // 어제 들어온 주문인지 보여주기 위함
            : ""}
        </div>
      </div>
      <div className="text-lg text-gray-600 w-full flex items-center justify-between">
        <span className="font-bold w-1/3">주문시간</span>
        <div className="w-2/3">
          {new Intl.DateTimeFormat("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).format(new Date(order.createdAt))}
        </div>
      </div>

      <div
        className={`pt-4 text-lg w-full flex items-center justify-between ${
          order.isPackConfirmed
            ? "text-green-500"
            : order.isCanceled
            ? "text-red-500"
            : "text-gray-600"
        }`}
      >
        <span className="font-bold w-1/3 tracking-wide">
          상&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;태
        </span>
        <span className="w-2/3">
          {order.isPackConfirmed ? (
            <span className="text-green-500">확정됨</span>
          ) : order.isCanceled ? (
            <span className="text-red-500">
              {"취소됨"}
              <br />
              {order.cancelReason && (
                <span className="text-gray-600">
                  {"(취소사유: " + order.cancelReason + ")"}
                </span>
              )}
            </span>
          ) : (
            <span className="text-gray-600">대기중</span>
          )}
        </span>
      </div>
      {order.isPackConfirmed && (
        <div className="flex pt-4 justify-center">
          <div className="flex items-center border px-2 py-1.5 rounded-lg bg-white gap-2">
            <span className="font-bold">손님이 픽업을 완료했나요?</span>
            <button
              className={`flex items-center justify-center w-12 h-6 rounded-full transition-colors duration-300 ${
                isPickupCompleted ? "bg-green-500" : "bg-gray-300"
              }`}
              onClick={() => {
                handleCompletePickup(order.id);
              }}
            >
              <span
                className={`w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${
                  isPickupCompleted
                    ? "translate-x-[12px]"
                    : "translate-x-[-12px]"
                }`}
              ></span>
            </button>
          </div>
        </div>
      )}
      <div className="flex justify-end mt-2">
        {!order.isPackConfirmed && !order.isCanceled && (
          <>
            {!isTomorrowOrder(order) && (
              <button
                className="bg-green-500 text-white px-3 py-1 rounded mr-2"
                onClick={() => {
                  handleConfirmOrder(order.id);
                }}
              >
                조기 확정
              </button>
            )}
            <button
              className="bg-red-500 text-white px-3 py-1 rounded"
              onClick={() => {
                handleCancelOrder(order.id);
              }}
            >
              주문 취소
            </button>
          </>
        )}
      </div>

      <button
        className="absolute bottom-0 left-0 text-xl w-full h-8 bg-blue-500 mt-2 text-white underline rounded-b-lg "
        onClick={() => {
          toggleDetails();
        }}
      >
        {isOpen ? "접기" : "자세히 보기"}
      </button>

      {isOpen && <div className="border-t border-gray-300 pt-6" />}

      {isOpen && (
        <div className="flex flex-col gap-4 pb-6">
          {/* 이전 주문갯수 */}
          <div className="flex text-xl items-center justify-between">
            <span className="text-gray-600 font-bold text-lg w-1/3">
              이전 주문갯수
            </span>
            <div className="text-black ml-2 w-2/3">
              {data?.getUserStoreSummary?.totalOrderCount || 0}회
            </div>
          </div>
          {/* 마지막 방문일자 */}
          <div className="flex text-xl items-center justify-between">
            <span className="text-gray-600 font-bold text-lg w-1/3">
              마지막 방문일자
            </span>
            <div className="text-black ml-2 w-2/3">
              {data?.getUserStoreSummary?.lastPickupDate || "없음"}
            </div>
          </div>

          {/* 이전리뷰 */}
          <div className="flex flex-col text-xl justify-center">
            {data?.getUserStoreSummary?.lastReview?.content ? (
              <>
                <span className="text-gray-600 font-bold text-lg w-1/3">
                  이전리뷰
                </span>
                {data?.getUserStoreSummary?.lastReview?.reviewImages.map(
                  (image: any) =>
                    image?.img && (
                      <img
                        key={image?.img}
                        src={image?.img}
                        alt="리뷰이미지"
                        className="w-40 h-40 rounded-lg"
                      />
                    )
                )}
                {data?.getUserStoreSummary?.lastReview?.content && (
                  <div className="text-black mt-2">
                    {data?.getUserStoreSummary?.lastReview?.content || "없음"}
                  </div>
                )}
              </>
            ) : (
              <div className="flex text-xl">
                <span className="text-gray-600 font-bold text-lg w-1/3">
                  이전리뷰
                </span>
                <span className="text-black ml-2">리뷰가 없습니다.</span>
              </div>
            )}
          </div>
          {/* 유저 차단 */}
          {onBlockUser && order.user?.id && (
            <div className="flex text-xl justify-end mt-4">
              <button
                className="text-red-500 font-bold ml-2 underline"
                onClick={() => {
                  if (order.user?.id) {
                    onBlockUser(order.user.id, order.user.name || undefined);
                  }
                }}
              >
                유저 차단
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default OrderCard;
